import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const ProjectEstimator = () => {
  const [projectType, setProjectType] = useState('');
  const [complexity, setComplexity] = useState('');
  const [timeline, setTimeline] = useState('');
  const [email, setEmail] = useState('');
  const [estimate, setEstimate] = useState(null);

  const projectTypes = [
    { value: 'mvp', label: 'MVP Development' },
    { value: 'full-product', label: 'Full Product Development' },
    { value: 'enhancement', label: 'Feature Enhancement' },
    { value: 'migration', label: 'Platform Migration' },
    { value: 'integration', label: 'System Integration' }
  ];

  const complexityLevels = [
    { value: 'simple', label: 'Simple - Basic features, standard UI' },
    { value: 'moderate', label: 'Moderate - Custom features, integrations' },
    { value: 'complex', label: 'Complex - Advanced architecture, AI/ML' }
  ];

  const timelineOptions = [
    { value: 'urgent', label: 'Urgent (2-3 months)' },
    { value: 'standard', label: 'Standard (4-6 months)' },
    { value: 'flexible', label: 'Flexible (6+ months)' }
  ];

  const calculateEstimate = () => {
    if (!projectType || !complexity || !timeline) return;

    const baseRates = {
      mvp: 50000,
      'full-product': 150000,
      enhancement: 30000,
      migration: 80000,
      integration: 40000
    };

    const complexityMultipliers = {
      simple: 1,
      moderate: 1.5,
      complex: 2.2
    };

    const timelineMultipliers = {
      urgent: 1.3,
      standard: 1,
      flexible: 0.9
    };

    const basePrice = baseRates?.[projectType];
    const complexityFactor = complexityMultipliers?.[complexity];
    const timelineFactor = timelineMultipliers?.[timeline];

    const estimatedCost = Math.round(basePrice * complexityFactor * timelineFactor);
    const estimatedDuration = complexity === 'simple' ? '2-3 months' : 
                             complexity === 'moderate' ? '4-6 months' : '6-9 months';

    setEstimate({
      cost: estimatedCost,
      duration: estimatedDuration,
      teamSize: complexity === 'simple' ? '2-3' : complexity === 'moderate' ? '4-5' : '6-8'
    });
  };

  return (
    <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-border rounded-lg p-6 md:p-8 lg:p-10">
      <div className="mb-6 md:mb-8">
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold text-foreground mb-3 md:mb-4">
          Project Estimation Calculator
        </h3>
        <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
          Get an instant ballpark estimate for your project. This tool provides preliminary cost and timeline projections based on your requirements.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div className="space-y-4 md:space-y-6">
          <Select
            label="Project Type"
            placeholder="Select project type"
            options={projectTypes}
            value={projectType}
            onChange={setProjectType}
            required
          />

          <Select
            label="Complexity Level"
            placeholder="Select complexity"
            options={complexityLevels}
            value={complexity}
            onChange={setComplexity}
            required
          />

          <Select
            label="Desired Timeline"
            placeholder="Select timeline"
            options={timelineOptions}
            value={timeline}
            onChange={setTimeline}
            required
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@company.com"
            description="Receive detailed estimate report"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
          />

          <Button
            variant="default"
            size="lg"
            fullWidth
            iconName="Calculator"
            iconPosition="left"
            onClick={calculateEstimate}
            disabled={!projectType || !complexity || !timeline}
          >
            Calculate Estimate
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 md:p-8">
          {estimate ? (
            <div className="space-y-6">
              <div className="text-center pb-6 border-b border-border">
                <div className="text-sm text-muted-foreground mb-2">Estimated Investment</div>
                <div className="text-4xl md:text-5xl font-headline font-bold text-primary">
                  ${estimate?.cost?.toLocaleString()}
                </div>
                <div className="text-xs md:text-sm text-muted-foreground mt-2">
                  *Preliminary estimate subject to detailed scope review
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Clock" size={20} className="text-primary" />
                    <span className="text-sm md:text-base text-foreground font-medium">Duration</span>
                  </div>
                  <span className="text-sm md:text-base text-foreground font-semibold">{estimate?.duration}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Users" size={20} className="text-primary" />
                    <span className="text-sm md:text-base text-foreground font-medium">Team Size</span>
                  </div>
                  <span className="text-sm md:text-base text-foreground font-semibold">{estimate?.teamSize} members</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Calendar" size={20} className="text-primary" />
                    <span className="text-sm md:text-base text-foreground font-medium">Start Date</span>
                  </div>
                  <span className="text-sm md:text-base text-foreground font-semibold">Within 2 weeks</span>
                </div>
              </div>

              <Button
                variant="outline"
                size="default"
                fullWidth
                iconName="MessageSquare"
                iconPosition="left"
                onClick={() => window.location.href = '/contact'}
              >
                Schedule Detailed Consultation
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-8 md:py-12">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4 md:mb-6">
                <Icon name="Calculator" size={32} color="var(--color-primary)" />
              </div>
              <h4 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                Ready to Estimate?
              </h4>
              <p className="text-sm md:text-base text-muted-foreground max-w-sm">
                Fill in the project details on the left to receive an instant preliminary estimate for your development needs.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectEstimator;